import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import ModalComponent from '..'
import { sectionContentTitle } from '../../../features/channel-curation/good-home/section'
import {
  ContentType,
  HomepageBannerContent,
  HomePageItemMix,
  ManualContent,
  SectionItem,
  SingleSection,
} from '../../../redux-toolkits/home-page/homePage.type'
import { SingleEssential } from '../../../redux-toolkits/lists/list.types'
import { Product } from '../../../redux-toolkits/products/products.type'
import { StrippedVendor } from '../../../redux-toolkits/vendors/vendors.types'
import { Article } from '../../../redux-toolkits/lists/list.types'
import { Button } from '../../button'
import { TabNav } from '../../nav/tabNav'
import { AddExploreForm } from './addExploreModal'
import { AddHomeBannerForm } from './addHomeBannerModal'
import { SelectCreators } from './selectCreatorModal'
import { SelectEssentials } from './selectEssentialModal'
import { SelectGoodList } from './selectGoodListModal'
import { SelectProduct } from './selectProductModal'

export const sectionContentHeaders: Record<ContentType, string> = {
  CATEGORIES: 'Select categories',
  CREATORS: 'Select creators',
  ESSENTIALS: 'Select essentials',
  GOOD_LISTS: 'Select good lists',
  PRODUCTS: 'Select products',
  MANUAL_ITEM: 'Add explore card',
  HOMEPAGE_BANNER: 'Add banner image',
}

export const SelectSectionContentModal = ({
  open,
  setOpen,
  section,
  contentTypes = [],
  maxSelection,
  onCloseCallback,
  onSuccessCallback,
  loading = false,
}: {
  open: boolean
  setOpen: (val: boolean) => void
  section: SingleSection
  contentTypes: ContentType[]
  maxSelection: Record<ContentType, number>
  onCloseCallback?: () => void
  onSuccessCallback?: (val: SectionItem[]) => void
  loading?: boolean
}) => {
  const [activeTab, setActiveTab] = useState<ContentType>(contentTypes[0])
  const [itemsObj, setItemsObj] = useState<
    Partial<
      Record<
        ContentType,
        (HomePageItemMix | ManualContent | HomepageBannerContent)[]
      >
    >
  >({
    CREATORS: [],
    PRODUCTS: [],
    ESSENTIALS: [],
    GOOD_LISTS: [],
    MANUAL_ITEM: [],
    HOMEPAGE_BANNER: [],
  })
  const [shouldClickSave, setShouldClickSave] = useState(false)

  const saveButtonRef = useRef<HTMLButtonElement | null>(null)

  const flattenItemsObj = () => {
    const sectionItemsFlat: SectionItem[] = Object.keys(itemsObj)
      .map((k) => {
        return {
          type: k as ContentType,
          items: itemsObj[k as ContentType],
        }
      })
      .filter((content) => content.items !== undefined)
      .flatMap((content) =>
        content.items!.map((item) => ({
          type: content.type,
          item:
            content.type === 'MANUAL_ITEM'
              ? undefined
              : (item as HomePageItemMix),
          manualContent:
            content.type === 'MANUAL_ITEM'
              ? (item as ManualContent)
              : undefined,
          homepageBanner:
            content.type === 'HOMEPAGE_BANNER'
              ? (item as HomepageBannerContent)
              : undefined,
        })),
      )

    return sectionItemsFlat
  }

  useEffect(() => {
    const sectItems = section?.items ?? []

    setItemsObj({
      CREATORS: sectItems
        .filter((i) => i.type === 'CREATORS')
        .map((i) => i.item as StrippedVendor),
      PRODUCTS: sectItems
        .filter((i) => i.type === 'PRODUCTS')
        .map((i) => i.item as Product),
      ESSENTIALS: sectItems
        .filter((i) => i.type === 'ESSENTIALS')
        .map((i) => i.item as SingleEssential),
      GOOD_LISTS: sectItems
        .filter((i) => i.type === 'GOOD_LISTS')
        .map((i) => i.item as Article),
      MANUAL_ITEM: sectItems
        .filter((i) => i.type === 'MANUAL_ITEM')
        .map((i) => i.manualContent as ManualContent),
      HOMEPAGE_BANNER: sectItems
        .filter((i) => i.type === 'HOMEPAGE_BANNER')
        .map((i) => i.homepageBanner as HomepageBannerContent),
    })
  }, [section])

  useEffect(() => {
    if (open) {
      setActiveTab(contentTypes[0])
    }
  }, [open])

  useEffect(() => {
    if (shouldClickSave && saveButtonRef.current) {
      saveButtonRef.current.click()
      setShouldClickSave(false) // reset the flag
    }
  }, [itemsObj, shouldClickSave])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback && onCloseCallback()
      }}
      title={undefined}
      childrenClassName="md:!max-w-[500px]"
    >
      <div className="flex flex-col gap-6">
        <TabNav
          activeTab={activeTab}
          setActiveTab={(tab) => {
            setActiveTab(tab as ContentType)
          }}
          tabs={contentTypes.map((ct) => {
            return {
              name: sectionContentTitle[ct],
              id: ct,
            }
          })}
          type="nav"
          className=""
        />

        <div className="flex flex-col gap-1">
          <p className="font-recoleta font-semibold text-2xl">
            {sectionContentHeaders[activeTab]}
          </p>
          {activeTab !== 'HOMEPAGE_BANNER' ? (
            <p className="text-black">
              {itemsObj[activeTab]?.length ?? 0} of {maxSelection[activeTab]}{' '}
              {activeTab === 'MANUAL_ITEM' ? 'added' : 'selected'}
            </p>
          ) : null}
        </div>

        {activeTab === 'CREATORS' ? (
          <SelectCreators
            maxSelection={maxSelection.CREATORS}
            open={activeTab === 'CREATORS'}
            creators={itemsObj.CREATORS as StrippedVendor[]}
            setCreators={(c) => {
              setItemsObj({
                ...itemsObj,
                CREATORS: c,
              })
            }}
          />
        ) : undefined}

        {activeTab === 'ESSENTIALS' ? (
          <SelectEssentials
            maxSelection={maxSelection.CREATORS}
            open={activeTab === 'ESSENTIALS'}
            essentials={itemsObj.ESSENTIALS as SingleEssential[]}
            setEssentials={(vals) => {
              setItemsObj({
                ...itemsObj,
                ESSENTIALS: vals,
              })
            }}
          />
        ) : undefined}

        {activeTab === 'PRODUCTS' ? (
          <SelectProduct
            maxSelection={maxSelection.PRODUCTS}
            open={activeTab === 'PRODUCTS'}
            products={itemsObj.PRODUCTS as Product[]}
            setProducts={(vals) => {
              setItemsObj({
                ...itemsObj,
                PRODUCTS: vals,
              })
            }}
          />
        ) : undefined}

        {activeTab === 'GOOD_LISTS' ? (
          <SelectGoodList
            maxSelection={maxSelection.GOOD_LISTS}
            open={activeTab === 'GOOD_LISTS'}
            goodLists={itemsObj.GOOD_LISTS as Article[]}
            setGoodLists={(c) => {
              setItemsObj({
                ...itemsObj,
                GOOD_LISTS: c,
              })
            }}
          />
        ) : undefined}

        {activeTab === 'MANUAL_ITEM' ? (
          <AddExploreForm
            onSuccessCallback={(content) => {
              setItemsObj({
                ...itemsObj,
                MANUAL_ITEM: [
                  ...(itemsObj.MANUAL_ITEM as ManualContent[]),
                  content as ManualContent,
                ],
              })
              setShouldClickSave(true)
            }}
            content={undefined}
            open={activeTab === 'MANUAL_ITEM'}
            mode="create"
            loading={loading}
            proceedText="Save"
          />
        ) : undefined}

        {activeTab === 'HOMEPAGE_BANNER' ? (
          <AddHomeBannerForm
            onSuccessCallback={(content) => {
              setItemsObj({
                ...itemsObj,
                HOMEPAGE_BANNER: [
                  ...(itemsObj.HOMEPAGE_BANNER as HomepageBannerContent[]),
                  content,
                ],
              })
              setShouldClickSave(true)
            }}
            content={undefined}
            open={activeTab === 'HOMEPAGE_BANNER'}
            mode="create"
            loading={loading}
            proceedText="Save"
          />
        ) : undefined}

        <Button
          type="button"
          primary
          label="Save"
          onClick={() => {
            const newSectionItems: SectionItem[] = flattenItemsObj()

            onSuccessCallback && onSuccessCallback(newSectionItems)
          }}
          loading={loading}
          disabled={loading || flattenItemsObj().length < 1}
          className={classNames('sticky bottom-0', {
            hidden:
              activeTab === 'MANUAL_ITEM' || activeTab === 'HOMEPAGE_BANNER',
          })}
          ref={saveButtonRef}
        />
      </div>
    </ModalComponent>
  )
}
